import * as React from 'react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { Plus } from 'lucide-react';

import { useSaveBatchMutation, useUpdateBatchMutation } from '@/store/batchApi';
import { useGetAllCourseQuery } from '@/store/courseApi';
import useFormData from '@/hooks/useFormData';
import { toast } from 'sonner';
import { useSelector } from 'react-redux';
import { RootState } from '@/types';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

interface BatchFormData {
  bCode: string;
  courseName: string;
  endTime: string;
  startTime: string;
  topic: string;
}

interface Batch {
  _id: number;
  bCode: string;
  courseName: string;
  startTime: string;
  endTime?: string;
  topic: string;
}

interface BatchDialogProps {
  data: Batch | undefined;
  setEdit: React.Dispatch<React.SetStateAction<Batch | null>>;
  dialogOpen: boolean;
  setDialogOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function BatchDialog({
  data,
  setEdit,
  dialogOpen,
  setDialogOpen,
}: BatchDialogProps) {
  const { user } = useSelector((state: RootState) => state.user);

  const [createBatch, { isLoading: isCreating }] = useSaveBatchMutation();
  const [updateBatch, { isLoading: isUpdating }] = useUpdateBatchMutation();
  const { data: courses } = useGetAllCourseQuery();

  const { formData, setFormData, resetForm, handleChange } =
    useFormData<BatchFormData>({
      bCode: '',
      courseName: '',
      startTime: '',
      endTime: '',
      topic: '',
    });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (data) {
      const updatedBatch = { id: data?._id, Token: user?.token, formData };
      try {
        await updateBatch(updatedBatch).unwrap();
        toast.success('Batch updated successfully!');
        setEdit(null);
        setDialogOpen(false);
      } catch (error) {
        toast.error('Failed to update batch!');
      }
    } else {
      const createdBatch = { Token: user?.token, formData };
      try {
        await createBatch(createdBatch).unwrap();
        toast.success('Batch created successfully!');
        resetForm();
        setDialogOpen(false);
      } catch (error) {
        toast.error('Failed to create batch!');
      }
    }
  };

  React.useEffect(() => {
    if (dialogOpen) {
      if (data) {
        setFormData({
          bCode: data.bCode,
          courseName: data?.courseName,
          startTime: data.startTime,
          endTime: data.endTime,
          topic: data.topic,
        });
      } else {
        resetForm();
      }
    }
  }, [dialogOpen, data]);

  return (
    <Dialog
      open={dialogOpen}
      onOpenChange={() => {
        setEdit(null);
        setDialogOpen(!dialogOpen);
      }}
    >
      {user?.role === 'hr' && (
        <DialogTrigger asChild>
          <Button className="h-11 px-4 bg-green-600 hover:bg-green-700 text-white transition-all duration-300 flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Create New Batch
          </Button>
        </DialogTrigger>
      )}

      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {data ? (
              <h3 className="text-foreground">Update Batch</h3>
            ) : (
              <h3 className="text-foreground">Create New Batch</h3>
            )}
          </DialogTitle>
          <DialogDescription>
            Fill in the details below to {data ? 'update' : 'create  a new'}{' '}
            batch.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="bCode" className="text-foreground">
                Batch Code
              </Label>
              <Input
                id="bCode"
                name="bCode"
                placeholder="e.g., MERN-2025-JAN"
                value={formData.bCode}
                onChange={handleChange}
                className="transition-all text-foreground duration-300 focus:ring-2 focus:ring-green-500"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="courseName" className="text-foreground">
                Course Name
              </Label>
              <Select
                value={formData.courseName}
                onValueChange={(value) =>
                  handleChange({ id: 'courseName', value })
                }
                required
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a Course" />
                </SelectTrigger>
                <SelectContent>
                  {courses?.map((course) => (
                    <SelectItem key={course._id} value={course.course}>
                      {course.course}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="topic" className="text-foreground">
                Batch Topic
              </Label>
              <Input
                id="topic"
                type="input"
                name="topic"
                placeholder="Flex box"
                value={formData.topic}
                onChange={handleChange}
                className="transition-all text-foreground duration-300 focus:ring-2 focus:ring-green-500"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="startTime" className="text-foreground">
                Start Time
              </Label>
              <Input
                id="startTime"
                name="startTime"
                type="time"
                value={formData.startTime}
                onChange={handleChange}
                className="transition-all duration-300 text-foreground focus:ring-2 focus:ring-green-500"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="endTime" className=" text-foreground">
                End Time
              </Label>
              <Input
                id="endTime"
                name="endTime"
                type="time"
                value={formData.endTime}
                onChange={handleChange}
                className="transition-all text-foreground duration-300 focus:ring-2 focus:ring-green-500"
              />
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setEdit(null), setDialogOpen(false);
              }}
              className="transition-all duration-300  hover:bg-gray-100 text-muted-foreground"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-green-600 hover:bg-green-700 text-white transition-all duration-300"
            >
              {isCreating || isUpdating ? (
                <>
                  <Loader2 className="animate-spin w-4 h-4" />
                  {data ? 'Updating Batch' : 'Creating Batch'}
                </>
              ) : data ? (
                'Update Batch'
              ) : (
                'Create Batch'
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
